import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, Phone } from "lucide-react";
import SmoothScroll from "@/components/site/SmoothScroll";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import Seo from "@/components/site/Seo";
import { Reveal, FadeUp } from "@/components/site/Reveal";
import { CONTACT } from "@/data/content";
import { useSiteContent } from "@/context/ContentContext";

export default function AreasIndex() {
  const { suburbs, categories } = useSiteContent();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const title = "Areas We Serve | Tiling Across Perth's Premier Suburbs | Perth Tiling WA";
  const description = "Perth Tiling WA services Perth's finest suburbs — Dalkeith, Cottesloe, City Beach, Fremantle, Applecross, Scarborough, Joondalup and more. Explore local tiling services in your area.";
  const keywords = "tiling Perth suburbs, tilers near me, tiling Dalkeith, tiling Cottesloe, tiling Fremantle, tiling Joondalup, tiling Applecross, tiling Scarborough";
  const breadcrumb = [
    { name: "Home", path: "/" },
    { name: "Areas We Serve", path: "/areas" },
  ];

  return (
    <SmoothScroll>
      <Seo pageKey="areas" overrideTitle={title} overrideDescription={description} overrideKeywords={keywords} breadcrumb={breadcrumb} />
      <div className="grain-overlay" aria-hidden />
      <div data-testid="areas-index-page" className="bg-[#f8fafc] min-h-screen">
        <Navbar />

        {/* Hero */}
        <section className="relative pt-40 pb-20 md:pt-52 md:pb-28 border-b border-[#0f172a]/10">
          <div className="mx-auto max-w-[1600px] px-6 md:px-12">
            <nav className="mb-8 flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#64748b]" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-[#9c7a4d] transition-colors">Home</Link>
              <span>/</span>
              <span className="text-[#0f172a]">Areas We Serve</span>
            </nav>
            <p className="overline !text-[#9c7a4d] mb-5 flex items-center gap-2">
              <MapPin className="h-3.5 w-3.5" /> Local to Perth
            </p>
            <h1 className="font-serif font-light text-[#0f172a] text-5xl sm:text-7xl lg:text-8xl leading-[0.95] tracking-tight">
              <Reveal load>Areas we</Reveal>
              <Reveal load delay={0.12}>serve.</Reveal>
            </h1>
            <FadeUp delay={0.2}>
              <p className="mt-8 max-w-2xl text-[#475569] text-lg md:text-xl font-light leading-relaxed">
                From riverside prestige to beachside character, we bring complete tiling craftsmanship to Perth's finest suburbs. Select your area for local service details, popular projects and answers to the questions your neighbours ask us most.
              </p>
            </FadeUp>
            <FadeUp delay={0.28}>
              <div className="mt-10 flex flex-wrap gap-4">
                <a href="/#enquire" data-testid="areas-index-enquire-cta" className="group inline-flex items-center gap-3 bg-[#9c7a4d] text-white hover:bg-[#0f172a] px-8 py-4 text-xs uppercase tracking-[0.22em] transition-colors">
                  Get a Free Quote <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </a>
                <a href={`tel:${CONTACT.phoneRaw}`} className="inline-flex items-center gap-3 border border-[#0f172a]/20 text-[#0f172a] hover:border-[#9c7a4d] hover:text-[#9c7a4d] px-8 py-4 text-xs uppercase tracking-[0.22em] transition-colors">
                  <Phone className="h-4 w-4" /> {CONTACT.phone}
                </a>
              </div>
            </FadeUp>
          </div>
        </section>

        {/* Suburb grid */}
        <section className="py-20 md:py-28">
          <div className="mx-auto max-w-[1600px] px-6 md:px-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {suburbs.map((s, i) => (
              <motion.div
                key={s.slug}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-6%" }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.06, ease: [0.22, 1, 0.36, 1] }}
              >
                <Link
                  to={`/tiling/${s.slug}`}
                  data-testid={`areas-index-card-${s.slug}`}
                  className="group block border border-[#0f172a]/10 bg-white hover:bg-[#0b1120] p-8 md:p-10 h-full transition-colors duration-500"
                >
                  <span className="overline !text-[#9c7a4d]">Tiling in {s.name}</span>
                  <h2 className="font-serif font-light text-3xl md:text-4xl text-[#0f172a] group-hover:text-white mt-4 transition-colors duration-500 flex items-center gap-3">
                    {s.name}
                    <ArrowRight className="h-5 w-5 text-[#c9a87c] opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all" />
                  </h2>
                  <p className="mt-4 text-[#64748b] group-hover:text-[#e2e8f0] font-light leading-relaxed transition-colors duration-500">
                    {s.name} is {s.blurb}
                  </p>
                </Link>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Services cross-link */}
        <section className="py-16 md:py-20 bg-[#f1f5f9] border-y border-[#0f172a]/10" data-testid="areas-index-services">
          <div className="mx-auto max-w-[1600px] px-6 md:px-12">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
              <div>
                <p className="overline !text-[#9c7a4d] mb-4">What We Do</p>
                <h2 className="font-serif font-light text-[#0f172a] text-2xl md:text-3xl leading-tight">
                  Every aspect of tiling, in every suburb
                </h2>
              </div>
              <Link to="/services" data-testid="areas-index-all-services-link" className="inline-flex items-center gap-2 text-[#64748b] hover:text-[#9c7a4d] text-sm uppercase tracking-[0.2em] transition-colors">
                View all services <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="flex flex-wrap gap-3">
              {categories.map((c) => (
                <Link key={c.id} to={`/services/${c.id}`} data-testid={`areas-index-service-${c.id}`} className="text-sm text-[#475569] hover:text-white hover:bg-[#9c7a4d] border border-[#0f172a]/15 hover:border-[#9c7a4d] px-4 py-2 transition-colors">
                  {c.title}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </SmoothScroll>
  );
}
